import { useEffect, useState } from "react";
import { Copy, Printer } from "lucide-react";
import { toast } from "sonner";
import BackOffice from "@/components/account/BackOffice";
import { PageHead } from "@/components/account/Shell";
import { Cell, Sheet, Txt } from "@/components/account/Sheet";
import api, { formatApiError } from "@/lib/api";

const FORM_COLS = [
    { key: "form", label: "Subject starts with", sub: "make a Gmail folder for each", w: "minmax(200px,1fr)" },
    { key: "who", label: "Who gets it", w: "minmax(180px,1fr)" },
    { key: "area", label: "Area code", sub: "#13-shop-AREA-n", w: "110px" },
    { key: "gmail", label: "Gmail search / filter", w: "minmax(240px,1.2fr)" },
];
const SHOP_COLS = [
    { key: "code", label: "Shop code", w: "110px" },
    { key: "name", label: "Shop", w: "minmax(180px,1fr)" },
    { key: "inbox", label: "Emails go to", w: "minmax(220px,1fr)" },
    { key: "gmail", label: "Gmail search / filter", w: "minmax(220px,1fr)" },
];

const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied"), () => {});

function Search({ text, testid }) {
    return (
        <Cell>
            <button type="button" onClick={() => copy(text)} data-testid={testid}
                className="inline-flex items-center gap-1.5 font-mono text-[12px] text-[#1a1a1a] hover:underline print:no-underline">
                {text} <Copy size={11} className="print:hidden" />
            </button>
        </Cell>
    );
}

export default function EmailGuidePage() {
    const [g, setG] = useState(null);
    useEffect(() => {
        api.get("/shop-inbox/email-guide").then((r) => setG(r.data)).catch((e) => toast.error(formatApiError(e)));
    }, []);
    return (
        <BackOffice>
            <PageHead kicker="Orders" title="Gmail filter guide" testid="email-guide-head"
                sub="Every email subject starts with its form name, so Gmail (or Grok) can sort them into folders automatically." />
            <div className="mt-3 space-y-5" data-testid="email-guide">
                <div className="flex flex-wrap items-start gap-4 border border-[#e3e3e3] bg-white p-4">
                    <div className="min-w-0 flex-1 text-[13px] text-[#303030]">
                        <p className="font-semibold">How a subject reads</p>
                        <p className="mt-1 font-mono text-[12px]" data-testid="email-guide-example">{g?.example}</p>
                        <p className="mt-1 text-[#616161]">{g?.shape}</p>
                        <p className="mt-1 text-[#616161]">{g?.customer_suffix}</p>
                        <p className="mt-2 text-[#616161]">In Gmail: paste a search below into the search bar, then click
                            <b> Show search options → Create filter → Apply the label</b>.</p>
                    </div>
                    <button type="button" onClick={() => window.print()} data-testid="email-guide-print"
                        className="bo-inv inline-flex items-center gap-1.5 bg-[#1a1a1a] px-3 py-2 text-[13px] font-semibold text-white hover:bg-[#333] print:hidden">
                        <Printer size={14} /> Print cheat-sheet
                    </button>
                </div>
                <Sheet cols={FORM_COLS} rows={g?.forms || []} rowKey={(r) => r.form} testid="email-guide-forms" minWidth={820}
                    rowTestid={(r) => `guide-form-${r.form.toLowerCase().replace(/\s+/g, "-")}`}
                    cells={(r) => ({
                        form: <Cell><Txt bold>{r.form}</Txt></Cell>,
                        who: <Cell><Txt sub>{r.who.join(" · ")}</Txt></Cell>,
                        area: <Cell><Txt mono>{r.area}</Txt></Cell>,
                        gmail: <Search text={r.gmail} testid={`guide-copy-${r.area}-${r.form.length}`} />,
                    })} />
                {(g?.shops || []).length > 0 && (
                    <Sheet cols={SHOP_COLS} rows={g.shops} rowKey={(s) => s.code} testid="email-guide-shops" minWidth={760}
                        rowTestid={(s) => `guide-shop-${s.code}`}
                        cells={(s) => ({
                            code: <Cell><Txt mono>{s.code}</Txt></Cell>,
                            name: <Cell><Txt bold>{s.name}</Txt></Cell>,
                            inbox: <Cell><Txt sub>{s.inbox}</Txt></Cell>,
                            gmail: <Search text={s.gmail} testid={`guide-shop-copy-${s.code}`} />,
                        })} />
                )}
            </div>
        </BackOffice>
    );
}
